/**
 * Color Mode Context
 *
 * Manages the user's color scheme preference (light | dark | system).
 * The resolved mode is derived from the preference:
 *   - 'light' / 'dark' → use directly
 *   - 'system'         → follow prefers-color-scheme media query
 *
 * Preference is persisted to localStorage under the key 'vellum:colorMode'.
 *
 * Usage:
 *   const { colorMode, setColorMode, resolvedMode } = useColorMode();
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PaletteMode } from '@mui/material/styles';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ColorModePreference = 'light' | 'dark' | 'system';

interface ColorModeContextValue {
  /** The user's stored preference ('light' | 'dark' | 'system') */
  colorMode: ColorModePreference;
  /** The actually applied mode after resolving 'system' */
  resolvedMode: PaletteMode;
  /** Update and persist the preference */
  setColorMode: (mode: ColorModePreference) => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

const STORAGE_KEY = 'vellum:colorMode';
const VALID_MODES: ColorModePreference[] = ['light', 'dark', 'system'];

function readStoredPreference(): ColorModePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_MODES.includes(stored as ColorModePreference)) {
      return stored as ColorModePreference;
    }
  } catch {
    // localStorage may be blocked (private browsing, etc.)
  }
  return 'system';
}

function getSystemMode(): PaletteMode {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [colorMode, setColorModeState] = useState<ColorModePreference>(readStoredPreference);
  const [systemMode, setSystemMode] = useState<PaletteMode>(getSystemMode);

  // Track OS-level preference changes in real time
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setColorMode = useCallback((mode: ColorModePreference) => {
    setColorModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore storage errors
    }
  }, []);

  const resolvedMode: PaletteMode = colorMode === 'system' ? systemMode : colorMode;

  // Sync <html data-color-mode> so non-MUI CSS can react to the mode
  useEffect(() => {
    document.documentElement.setAttribute('data-color-mode', resolvedMode);
  }, [resolvedMode]);

  const value = useMemo(
    () => ({ colorMode, setColorMode, resolvedMode }),
    [colorMode, setColorMode, resolvedMode],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used within a <ColorModeProvider>');
  }
  return ctx;
}
